import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { InvoiceItem } from '../types/Invoice';
export function InvoiceForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('id');
  const isEditMode = !!invoiceId;
  const [formData, setFormData] = useState({
    invoiceNumber: isEditMode ? 'INV-2024-001' : '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as const
  });
  const [items, setItems] = useState<InvoiceItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  const calculateItemTotal = (quantity: number, unitPrice: number) => {
    return quantity * unitPrice;
  };
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would save to an API
    console.log('Saving invoice:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const handleSaveAsDraft = () => {
    // In a real app, this would save as draft to an API
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  return <div className="space-y-6" data-id="element-7116">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-7117">
        <button onClick={() => navigate('/invoices')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-7118">
          <ArrowLeftIcon size={20} data-id="element-7119" />
          Back to Invoices
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-7120">
        {/* Main Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7121">
          <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-7122">
            {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
          </h2>
          {/* Invoice Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-7123">
            <div data-id="element-7124">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7125">
                Invoice Number
              </label>
              <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({
              ...formData,
              invoiceNumber: e.target.value
            })} placeholder="Auto-generated if left empty" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7126" />
            </div>
            <div data-id="element-7127">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7128">
                Status
              </label>
              <select value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as 'draft' | 'sent'
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7129">
                <option value="draft" data-id="element-7130">Draft</option>
                <option value="sent" data-id="element-7131">Send to Customer</option>
              </select>
            </div>
          </div>
          {/* Customer Information */}
          <div className="mb-6" data-id="element-7132">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-7133">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7134">
              <div data-id="element-7135">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7136">
                  Customer Name *
                </label>
                <input type="text" required value={formData.customerName} onChange={e => setFormData({
                ...formData,
                customerName: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7137" />
              </div>
              <div data-id="element-7138">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7139">
                  Email *
                </label>
                <input type="email" required value={formData.customerEmail} onChange={e => setFormData({
                ...formData,
                customerEmail: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7140" />
              </div>
              <div data-id="element-7141">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7142">
                  Phone
                </label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({
                ...formData,
                customerPhone: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7143" />
              </div>
            </div>
          </div>
          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-7144">
            <div data-id="element-7145">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7146">
                Issue Date *
              </label>
              <input type="date" required value={formData.issueDate} onChange={e => setFormData({
              ...formData,
              issueDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7147" />
            </div>
            <div data-id="element-7148">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7149">
                Due Date *
              </label>
              <input type="date" required value={formData.dueDate} onChange={e => setFormData({
              ...formData,
              dueDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7150" />
            </div>
          </div>
          <div className="mb-6" data-id="element-7151">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7152">
              Description
            </label>
            <input type="text" value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} placeholder="Brief description of the invoice" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7153" />
          </div>
        </div>
        {/* Invoice Items */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7154">
          <div className="flex items-center justify-between mb-4" data-id="element-7155">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7156">Invoice Items</h3>
            <button type="button" onClick={addItem} className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-7157">
              <PlusIcon size={18} data-id="element-7158" />
              Add Item
            </button>
          </div>
          <div className="space-y-4" data-id="element-7159">
            {items.map((item, index) => <div key={item.id} className="grid grid-cols-12 gap-4 items-start" data-id="element-7160">
                <div className="col-span-12 md:col-span-5" data-id="element-7161">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7162">
                    Description
                  </label>
                  <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Item description" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7163" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7164">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7165">
                    Quantity
                  </label>
                  <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7166" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7167">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7168">
                    Unit Price
                  </label>
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7169" />
                </div>
                <div className="col-span-3 md:col-span-2" data-id="element-7170">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7171">
                    Total
                  </label>
                  <div className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900" data-id="element-7172">
                    MYR {item.total.toLocaleString('en-MY')}
                  </div>
                </div>
                <div className="col-span-1 flex items-end" data-id="element-7173">
                  <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7174">
                    <TrashIcon size={20} data-id="element-7175" />
                  </button>
                </div>
              </div>)}
          </div>
          {/* Total */}
          <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-7176">
            <div className="flex justify-end" data-id="element-7177">
              <div className="w-full max-w-xs space-y-2" data-id="element-7178">
                <div className="flex justify-between py-2" data-id="element-7179">
                  <span className="text-sm text-gray-600" data-id="element-7180">Subtotal:</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-7181">
                    MYR {calculateSubtotal().toLocaleString('en-MY')}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-7182">
                  <span className="text-lg font-semibold text-gray-900" data-id="element-7183">
                    Total:
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-7184">
                    MYR {calculateSubtotal().toLocaleString('en-MY')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7185">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7186">
            Notes
          </label>
          <textarea value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={4} placeholder="Additional notes or payment terms..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-7187" />
        </div>
        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3" data-id="element-7188">
          <button type="button" onClick={() => navigate('/invoices')} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7189">
            Cancel
          </button>
          <button type="button" onClick={handleSaveAsDraft} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7190">
            Save as Draft
          </button>
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-7191">
            {isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>;
}